import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import {
  ExternalServiceProvider,
  ExternalServiceProviderCreate,
  ExternalServiceProviderUpdate,
  ExternalServiceProviderDocument
} from '../../../models/ExternalServiceProvider';
import { ExternalServiceProvidersService } from '../../../services/external-service-providers.service';
import {
  DocumentName,
  DocumentType,
  getBrazilianStates,
  getServiceTypes,
  getServiceTypeCssClass,
  getVehicleTypes,
  getDocumentTypes
} from '../../../models/ExternalServiceProviderEnums';

interface DocumentFormData {
  documentName: string;
  documentType: string;
  file: File | null;
  id?: number; // ID do documento existente (para edição)
  documentUrl?: string; // URL do documento existente (para exibição)
}

@Component({
  selector: 'app-external-service-providers-form',
  templateUrl: './external-service-providers-form.component.html',
  styleUrls: ['./external-service-providers-form.component.css']
})
export class ExternalServiceProvidersFormComponent implements OnInit {
  form!: FormGroup;
  providerId?: number;
  isSubmitting = false;
  isLoading = false;

  // Opções para os selects
  statesOptions = getBrazilianStates();
  serviceTypesOptions = getServiceTypes();
  vehicleTypesOptions = getVehicleTypes();
  documentTypesOptions = getDocumentTypes();

  get selectedServiceTypeClass(): string {
    return getServiceTypeCssClass(this.form?.get('serviceType')?.value);
  }
  
  // Array com 6 documentos por padrão
  documents: DocumentFormData[] = [
    { documentName: DocumentName.ALVARA_FUNCIONAMENTO, documentType: DocumentType.ALVARA, file: null },
    { documentName: DocumentName.CERTIFICADO_MEI, documentType: DocumentType.CERTIFICADO, file: null },
    { documentName: DocumentName.COMPROVANTE_REGULARIDADE_FISCAL, documentType: DocumentType.FISCAL, file: null },
    { documentName: DocumentName.SEGURO_RESPONSABILIDADE_CIVIL, documentType: DocumentType.SEGURO, file: null },
    { documentName: DocumentName.LICENCA_AMBIENTAL, documentType: DocumentType.LICENCA, file: null },
    { documentName: DocumentName.REGISTRO_CONSELHO_REGIONAL, documentType: DocumentType.REGISTRO, file: null }
  ];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private externalServiceProvidersService: ExternalServiceProvidersService
  ) {
    this.buildForm();
  }

  ngOnInit(): void {
    const idParam = this.route.snapshot.paramMap.get('id');
    // eslint-disable-next-line no-console
    if (idParam) {
      this.providerId = Number(idParam);
      // eslint-disable-next-line no-console
      this.loadProvider(this.providerId);
    } else {
      // eslint-disable-next-line no-console
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      tradeName: ['', Validators.required],
      companyName: ['', Validators.required],
      cnpj: [''],
      fullAddress: [''],
      phone: ['', this.phoneValidator],
      contactEmail: ['', Validators.email],
      responsibleName: [''],
      serviceType: [''],
      servedStates: [[], [Validators.required, this.atLeastOneUfValidator]],
      supportedVehicleTypes: [''],
      businessHours: [''],
      pixKey: ['']
    });
  }

  /** Exige ao menos uma UF (valor de 2 letras). */
  private atLeastOneUfValidator = (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    const ufs = Array.isArray(raw)
      ? raw
      : (typeof raw === 'string' ? raw.split(',').map(s => s.trim()).filter(Boolean) : []);
    return ufs.length > 0 ? null : { required: true };
  };

  private loadProvider(id: number): void {
    this.isLoading = true;
    this.externalServiceProvidersService.getById(id).subscribe({
      next: (provider: ExternalServiceProvider) => {
      const servedStatesValue = this.normalizeUfList(provider.servedStates);
      const supportedVehicleTypesValue = provider.supportedVehicleTypes || '';
      
      this.form.patchValue({
        tradeName: provider.tradeName,
        companyName: provider.companyName,
        cnpj: this.applyCnpjMask(provider.cnpj || ''),
        fullAddress: provider.fullAddress,
        phone: this.applyPhoneMask(provider.phone || ''),
        contactEmail: provider.contactEmail,
        responsibleName: provider.responsibleName,
        serviceType: provider.serviceType,
        servedStates: servedStatesValue,
        supportedVehicleTypes: supportedVehicleTypesValue,
        businessHours: provider.businessHours,
        pixKey: provider.pixKey || ''
      });
      
      // Carregar documentos existentes se houver
      // CRÍTICO: O backend retorna os documentos na ordem correta (índice 0 = document0, índice 1 = document1, etc.)
      // IMPORTANTE: Mapear os documentos do backend para os slots do formulário pela ORDEM (índice)
      // Cada documento do backend corresponde ao slot do mesmo índice no formulário
      if (provider.documents && provider.documents.length > 0) {
        // Primeiro, resetar todos os IDs e URLs para garantir que não haja dados antigos
        this.documents.forEach(doc => {
          doc.id = undefined;
          doc.documentUrl = undefined;
        });
        
        // Mapear cada documento do backend para o slot do mesmo índice no formulário
        // O backend retorna os documentos na ordem: [document0, document1, document2, document3, document4, document5]
        provider.documents.forEach((backendDoc, backendIndex) => {
          // Garantir que o índice não ultrapasse o tamanho do array do formulário (máximo 6)
          if (backendIndex < this.documents.length) {
            // Atualizar o slot com os dados do backend
            // Manter o nome padrão do formulário se o backend não tiver nome, mas atualizar se existir
            if (backendDoc.documentName) {
              this.documents[backendIndex].documentName = backendDoc.documentName;
            }
            if (backendDoc.documentType) {
              this.documents[backendIndex].documentType = backendDoc.documentType;
            }
            // CRÍTICO: Carregar ID e URL para manter documentos existentes
            // O ID é essencial para o backend saber qual documento editar/preservar
            this.documents[backendIndex].id = backendDoc.id;
            this.documents[backendIndex].documentUrl = backendDoc.documentUrl;
          }
        });
        
        // Log para debug - mostrar a ordem dos documentos
        // eslint-disable-next-line no-console
      } else {
        // Se não há documentos do backend, garantir que os IDs estejam limpos
        this.documents.forEach(doc => {
          doc.id = undefined;
          doc.documentUrl = undefined;
        });
      }
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      }
    });
  }

  onFileSelected(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.documents[index].file = file;
      // eslint-disable-next-line no-console
    } else {
      // eslint-disable-next-line no-console
      this.documents[index].file = null;
    }
  }

  removeFile(index: number): void {
    this.documents[index].file = null;
    // Resetar o input file
    const input = document.getElementById(`fileInput${index}`) as HTMLInputElement;
    if (input) {
      input.value = '';
    }
  }

  hasFilesToUpload(): boolean {
    return this.documents.some(doc => doc.file !== null);
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;

    // Se houver arquivos para upload, usar FormData (multipart/form-data)
    if (this.hasFilesToUpload()) {
      this.saveWithFiles();
    } else {
      // Se não houver arquivos, usar JSON simples
      this.saveWithoutFiles();
    }
  }

  private saveWithFiles(): void {
    const formData = new FormData();
    
    // Preparar o JSON do providerData (removendo campos vazios/undefined para manter JSON limpo)
    // IMPORTANTE: O backend espera "servedRegions" (não "servedStates")
    const servedStatesFormValue = this.form.value.servedStates;
    const servedRegionsValue = this.formatArrayToString(servedStatesFormValue);
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console
    const providerData: any = {
      tradeName: this.form.value.tradeName || '',
      companyName: this.form.value.companyName || '',
      cnpj: this.getCnpjPayloadValue(),
      fullAddress: this.form.value.fullAddress || undefined,
      phone: this.onlyDigits(this.form.value.phone || '') || undefined, // Remover máscara
      contactEmail: this.form.value.contactEmail || undefined,
      responsibleName: this.form.value.responsibleName || undefined,
      serviceType: this.form.value.serviceType || undefined,
      servedRegions: servedRegionsValue, // Backend espera "servedRegions" (não "servedStates")
      supportedVehicleTypes: this.formatArrayToString(this.form.value.supportedVehicleTypes),
      businessHours: this.form.value.businessHours || undefined,
      pixKey: (this.form.value.pixKey || '').trim() || undefined,
      // CRÍTICO: Sempre enviar TODOS os 6 documentos no array, na ordem correta (document0, document1, etc.)
      // O backend precisa receber todos os documentos (com seus IDs quando existirem) para manter os existentes
      // IMPORTANTE: Se um documento tem ID, SEMPRE incluir o ID no payload, mesmo que haja arquivo novo
      // O ID é essencial para o backend saber qual documento ATUALIZAR (não criar novo)
      documents: this.documents.map((doc, index) => {
        const docPayload: any = {
          documentName: doc.documentName || '',
          documentType: doc.documentType || ''
        };
        
        // CRÍTICO: Incluir ID se o documento existir no backend (mesmo que haja arquivo novo)
        // O ID é essencial para o backend saber qual documento ATUALIZAR ao invés de criar novo
        // Se há arquivo novo + ID existente → backend deve ATUALIZAR o documento existente
        // Se há arquivo novo + SEM ID → backend deve CRIAR novo documento
        if (doc.id) {
          docPayload.id = doc.id;
        }
        
        // eslint-disable-next-line no-console
        
        return docPayload;
      })
    };

    // Remover campos undefined do objeto (para não enviar no JSON)
    const cleanProviderData = Object.fromEntries(
      Object.entries(providerData).filter(([_, v]) => v !== undefined)
    );

    // Garantir que documents sempre existe (mesmo que vazio)
    if (!cleanProviderData['documents']) {
      cleanProviderData['documents'] = [];
    }

    // Adicionar providerData como texto (JSON stringificado)
    const jsonString = JSON.stringify(cleanProviderData, null, 2);
    formData.append('providerData', jsonString);
    
    // Log para debug (mostra o JSON exato que será enviado)
    // eslint-disable-next-line no-console

    // Adicionar os arquivos (document0 até document5) - SEMPRE NA ORDEM CORRETA
    // CRÍTICO: O backend espera que os arquivos sejam enviados na mesma ordem do array documents
    // document0 sempre será o primeiro, document1 o segundo, etc.
    // 
    // REGRA DE NEGÓCIO:
    // 1. Se há arquivo novo selecionado → SEMPRE enviar no FormData (substitui existente ou cria novo)
    // 2. Se NÃO há arquivo novo mas há documento existente (com id) → NÃO enviar no FormData (mantém existente)
    // 3. Se NÃO há arquivo novo nem documento existente → NÃO enviar no FormData (slot vazio)
    let filesCount = 0;
    this.documents.forEach((doc, index) => {
      // CRÍTICO: Se o documento tem ID, ele EXISTE no backend (mesmo que não tenha URL)
      // A URL pode não estar disponível por vários motivos, mas o ID indica que o documento existe
      const temDocumentoExistente = !!doc.id; // Basta ter ID para considerar existente
      const temArquivoNovo = !!(doc.file && doc.file instanceof File);
      
      // eslint-disable-next-line no-console
      
      if (temArquivoNovo && doc.file) {
        // HÁ ARQUIVO NOVO → SEMPRE enviar no FormData
        // IMPORTANTE: Se o documento tem ID, o backend deve ATUALIZAR o documento existente
        // Se o documento NÃO tem ID, o backend deve CRIAR um novo documento
        // O ID está sendo enviado no providerData para o backend saber qual documento atualizar
        formData.append(`document${index}`, doc.file, doc.file.name);
        filesCount++;
        // eslint-disable-next-line no-console
      } else if (temDocumentoExistente) {
        // NÃO HÁ ARQUIVO NOVO mas HÁ DOCUMENTO EXISTENTE (tem ID) → NÃO enviar no FormData
        // O ID no providerData é suficiente para o backend manter o arquivo existente
        // eslint-disable-next-line no-console
      } else {
        // NÃO HÁ ARQUIVO NOVO nem DOCUMENTO EXISTENTE (sem ID) → Slot vazio
        // eslint-disable-next-line no-console
      }
    });
    
    // eslint-disable-next-line no-console
    
    // Log do FormData para debug
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console
    // eslint-disable-next-line no-console

    if (this.providerId) {
      // CRÍTICO: Para edição com arquivos, usar FormData (PUT com multipart/form-data)
      // O endpoint de EDIÇÃO é: PUT /edit/external-service-providers/{id}
      // eslint-disable-next-line no-console
      this.externalServiceProvidersService.updateWithFiles(this.providerId, formData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/b2b/external-service-providers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    } else {
      // Criar com upload de arquivos
      // eslint-disable-next-line no-console
      this.externalServiceProvidersService.createWithFiles(formData).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/b2b/external-service-providers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  private saveWithoutFiles(): void {
    // Preparar payload removendo campos undefined
    const formValue = this.form.value;
    // IMPORTANTE: O backend espera "servedRegions" (não "servedStates")
    const servedRegionsValue = this.formatArrayToString(formValue.servedStates);
    const payload: any = {
      tradeName: formValue.tradeName || '',
      companyName: formValue.companyName || '',
      cnpj: this.getCnpjPayloadValue(formValue.cnpj),
      fullAddress: formValue.fullAddress || undefined,
      phone: this.onlyDigits(formValue.phone || '') || undefined, // Remover máscara
      contactEmail: formValue.contactEmail || undefined,
      responsibleName: formValue.responsibleName || undefined,
      serviceType: formValue.serviceType || undefined,
      servedRegions: servedRegionsValue, // Backend espera "servedRegions" (não "servedStates")
      supportedVehicleTypes: this.formatArrayToString(formValue.supportedVehicleTypes),
      businessHours: formValue.businessHours || undefined,
      pixKey: (formValue.pixKey || '').trim() || undefined,
      documents: this.documents
        .filter(doc => {
          // Incluir apenas documentos que têm arquivo novo OU documento existente (com id)
          // Não incluir documentos vazios (sem arquivo novo e sem documento existente)
          return (doc.file && doc.file instanceof File) || (doc.id && doc.documentUrl);
        })
        .map(doc => ({
          id: doc.id, // Incluir ID para documentos existentes (edição)
          documentName: doc.documentName || '',
          documentType: doc.documentType || ''
        }))
    };

    // Remover campos undefined
    const cleanPayload = Object.fromEntries(
      Object.entries(payload).filter(([_, v]) => v !== undefined)
    ) as unknown as ExternalServiceProviderCreate;

    // Garantir que documents sempre existe
    if (!cleanPayload.documents) {
      cleanPayload.documents = [];
    }

    // Log para debug
    // eslint-disable-next-line no-console

    if (this.providerId) {
      this.externalServiceProvidersService.update(this.providerId, cleanPayload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/b2b/external-service-providers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    } else {
      this.externalServiceProvidersService.create(cleanPayload).subscribe({
        next: () => {
          this.isSubmitting = false;
          this.router.navigate(['/b2b/external-service-providers']);
        },
        error: () => {
          this.isSubmitting = false;
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/b2b/external-service-providers']);
  }

  /**
   * Converte array em string separada por vírgulas, ou retorna a string/undefined original
   */
  private formatArrayToString(value: any): string | undefined {
    if (!value) return undefined;

    if (Array.isArray(value) || typeof value === 'string') {
      const ufs = this.normalizeUfList(value);
      return ufs.length > 0 ? ufs.join(', ') : undefined;
    }

    return value;
  }

  /** Converte "SP", "São Paulo", "SP - São Paulo" etc. em lista de UFs. */
  private normalizeUfList(value: string | string[] | null | undefined): string[] {
    const tokens = Array.isArray(value)
      ? value
      : (value || '').split(/[,;|/]/).map(s => s.trim()).filter(Boolean);

    const known = new Set(this.statesOptions.map(s => s.value));
    const nameToUf = new Map(
      this.statesOptions.map(s => {
        const name = (s.label.split(' - ')[1] || '').toLowerCase();
        return [name, s.value] as [string, string];
      })
    );

    const result: string[] = [];
    for (const raw of tokens) {
      const upper = raw.trim().toUpperCase();
      if (known.has(upper)) {
        if (!result.includes(upper)) result.push(upper);
        continue;
      }
      const ufPrefix = upper.slice(0, 2);
      if (known.has(ufPrefix) && (upper.length === 2 || ' -('.includes(upper[2]))) {
        if (!result.includes(ufPrefix)) result.push(ufPrefix);
        continue;
      }
      const paren = upper.match(/\(([A-Z]{2})\)/);
      if (paren && known.has(paren[1])) {
        if (!result.includes(paren[1])) result.push(paren[1]);
        continue;
      }
      const byName = nameToUf.get(raw.trim().toLowerCase());
      if (byName && !result.includes(byName)) result.push(byName);
    }
    return result;
  }

  private stringToArray(value: string | undefined): string[] | undefined {
    if (!value) return undefined;
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    return Array.isArray(value) ? value : undefined;
  }

  getSelectedStates(): string[] {
    const stateValues = this.normalizeUfList(this.form?.get('servedStates')?.value);
    return stateValues.map(stateValue => {
      const stateOption = this.statesOptions.find(s => s.value === stateValue);
      return stateOption ? stateOption.label : stateValue;
    });
  }

  removeState(stateLabel: string): void {
    const stateValue = this.statesOptions.find(s => s.label === stateLabel)?.value
      || this.statesOptions.find(s => s.value === stateLabel)?.value;
    if (!stateValue) return;

    const current = this.normalizeUfList(this.form?.get('servedStates')?.value);
    this.form?.get('servedStates')?.setValue(current.filter(v => v !== stateValue));
    this.form?.get('servedStates')?.markAsTouched();
  }

  /**
   * Obtém os tipos de veículos selecionados (para exibição)
   */
  getSelectedVehicleTypes(): string[] {
    const value = this.form?.get('supportedVehicleTypes')?.value;
    if (!value) return [];
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
    }
    return [];
  }

  /**
   * Remove um tipo de veículo da seleção
   */
  removeVehicleType(vehicleType: string): void {
    const currentValue = this.form?.get('supportedVehicleTypes')?.value || [];
    
    let newValue: string[];
    if (Array.isArray(currentValue)) {
      newValue = currentValue.filter(v => v !== vehicleType);
    } else if (typeof currentValue === 'string') {
      const arr = currentValue.split(',').map(s => s.trim());
      newValue = arr.filter(v => v !== vehicleType);
    } else {
      newValue = [];
    }

    this.form?.get('supportedVehicleTypes')?.setValue(newValue);
  }

  // ===== Máscaras =====

  /**
   * Aplica máscara de CNPJ: 00.000.000/0000-00 (sem validar dígitos).
   */
  applyCnpjMask(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 14);
    if (digits.length === 0) return '';

    return digits
      .replace(/(\d{2})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1/$2')
      .replace(/(\d{4})(\d{1,2})$/, '$1-$2');
  }

  /**
   * Aplica máscara de telefone: (00) 0000-0000 ou (00) 00000-0000
   */
  applyPhoneMask(value: string): string {
    const digits = this.onlyDigits(value).slice(0, 11);
    if (digits.length === 0) return '';
    
    if (digits.length <= 10) {
      // (00) 0000-0000
      return digits
        .replace(/(\d{2})(\d)/, '($1) $2')
        .replace(/(\d{4})(\d)/, '$1-$2');
    }
    // (00) 00000-0000
    return digits
      .replace(/(\d{2})(\d)/, '($1) $2')
      .replace(/(\d{5})(\d)/, '$1-$2');
  }

  /**
   * Remove todos os caracteres não numéricos
   */
  private onlyDigits(value: string): string {
    return (value || '').replace(/\D+/g, '');
  }

  /**
   * Handler para input de CNPJ — só formata; não valida se o CNPJ é válido.
   */
  onCnpjInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.applyCnpjMask(input.value);
    input.value = masked;
    this.form.get('cnpj')?.setValue(masked, { emitEvent: false });
  }

  /**
   * Handler para input de telefone
   */
  onPhoneInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const masked = this.applyPhoneMask(input.value);
    input.value = masked;
    this.form.get('phone')?.setValue(masked, { emitEvent: false });
    this.form.get('phone')?.updateValueAndValidity();
  }

  // ===== Validadores =====

  /**
   * Validador de telefone
   */
  phoneValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // Campo opcional
    }

    const digits = this.onlyDigits(value);
    
    // Telefone deve ter 10 ou 11 dígitos (com DDD)
    if (digits.length < 10 || digits.length > 11) {
      return { invalidPhone: true, message: 'Telefone deve ter 10 ou 11 dígitos (com DDD)' };
    }

    // Verificar se começa com 0 (inválido)
    if (digits.startsWith('0')) {
      return { invalidPhone: true, message: 'Telefone inválido' };
    }

    return null;
  };

  // ===== Getters para mensagens de erro =====

  /** CNPJ sem máscara no payload; vazio se não informado. Sem validação de dígitos. */
  private getCnpjPayloadValue(cnpjFromForm?: string): string {
    return this.onlyDigits(cnpjFromForm ?? this.form.value.cnpj ?? '');
  }

  get phoneError(): string | null {
    const phoneControl = this.form.get('phone');
    if (phoneControl?.errors && phoneControl.touched) {
      if (phoneControl.errors['invalidPhone']) {
        return phoneControl.errors['message'] || 'Telefone inválido';
      }
    }
    return null;
  }

}


